import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Upload, Wallet, PiggyBank, TrendingUp, Receipt, FileText, Sparkles, Brain, Database } from 'lucide-react';
import GlassPanel from '../../components/GlassPanel';
import EmptyState from '../../components/EmptyState';
import { AIPanel, AIButton, AIExplanationBox, AIBadge } from '../../components/AIComponents';

const financeSubNav = [
  { path: '/finance', label: 'Overview', icon: Wallet },
  { path: '/finance/pots', label: 'Money Pots', icon: PiggyBank },
  { path: '/finance/cashflow', label: 'Cashflow', icon: TrendingUp },
  { path: '/finance/invoices', label: 'Invoices', icon: Receipt },
  { path: '/finance/suppliers', label: 'Supplier Upload', icon: Upload },
];

const mockSupplierInvoices = [
  { id: 1, supplier: 'ABC Building Supplies', amount: 450.00, date: '2024-01-14', status: 'processed' },
  { id: 2, supplier: 'Plumbing Wholesale Ltd', amount: 280.00, date: '2024-01-12', status: 'processed' },
  { id: 3, supplier: 'Electrical Parts Co', amount: 165.00, date: '2024-01-10', status: 'pending' },
];

export function SupplierUpload() {
  const location = useLocation();

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Supplier Invoice Upload</h1>
          <p>Upload supplier invoices to build your Trade Pricing Intelligence</p>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="flex gap-sm mb-xl" style={{ flexWrap: 'wrap' }}>
        {financeSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
            style={{ gap: 'var(--space-sm)' }}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      {/* AI Explanation Panel - IMPORTANT */}
      <AIPanel 
        title="AI Pricing Intelligence" 
        description="When supplier invoices are uploaded, AI extracts pricing data to improve future quote accuracy and build your Trade Pricing Intelligence."
        className="mb-xl"
      >
        <div className="ai-actions-grid single-col">
          <AIButton icon={Sparkles} variant="full" tooltip="AI generation will be enabled soon">
            Analyse supplier invoice (AI)
          </AIButton>
        </div>
      </AIPanel>

      {/* Upload Area */}
      <GlassPanel className="mb-xl">
        <div style={{ 
          border: '2px dashed var(--glass-border)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-3xl)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all var(--transition-base)'
        }}>
          <Upload size={48} style={{ color: 'var(--accent-primary)', marginBottom: 'var(--space-md)' }} />
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-xs)' }}>
            Drop supplier invoices here
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
            or click to browse files
          </p>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-dimmed)' }}>
            Supports PDF, JPG, PNG • Max 10MB per file
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)', marginTop: 'var(--space-lg)' }}>
            <AIBadge text="AI Extraction" />
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              AI will extract pricing data automatically
            </span>
          </div>
        </div>
      </GlassPanel>

      {/* How it works */}
      <GlassPanel title="How AI Pricing Intelligence Works" titleIcon={Brain} className="mb-xl">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-lg)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 'var(--radius-full)',
              background: 'var(--accent-primary-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-md)',
              color: 'var(--accent-primary)'
            }}>
              <Upload size={24} />
            </div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-xs)' }}>
              1. Upload Invoice
            </h4>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Upload supplier invoices as you receive them
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 'var(--radius-full)',
              background: 'var(--accent-primary-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-md)',
              color: 'var(--accent-primary)'
            }}>
              <Sparkles size={24} />
            </div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-xs)' }}>
              2. AI Extracts Data
            </h4>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              AI reads and extracts pricing and product information
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 'var(--radius-full)',
              background: 'var(--accent-primary-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-md)',
              color: 'var(--accent-primary)'
            }}>
              <Database size={24} />
            </div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-xs)' }}>
              3. Builds Intelligence
            </h4>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Data improves quote accuracy over time
            </p>
          </div>
        </div>
      </GlassPanel>

      {/* Recent Uploads */}
      <GlassPanel title="Recent Supplier Invoices" titleIcon={FileText}>
        {mockSupplierInvoices.length > 0 ? (
          <div className="list-container">
            {mockSupplierInvoices.map((invoice) => (
              <div key={invoice.id} className="list-item" style={{ cursor: 'default' }}>
                <div className="list-item-content">
                  <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    {invoice.supplier}
                    {invoice.status === 'processed' && <AIBadge text="AI Processed" />}
                  </div>
                  <div className="list-item-subtitle">{invoice.date}</div>
                </div>
                <div className="list-item-meta">
                  <span className={`status-badge ${invoice.status === 'processed' ? 'completed' : 'pending'}`}>
                    {invoice.status}
                  </span>
                  <span className="list-item-amount">£{invoice.amount.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState 
            icon={Upload}
            title="No supplier invoices"
            description="Upload your first supplier invoice to start building pricing intelligence"
          />
        )}
      </GlassPanel>

      <AIExplanationBox>
        Your supplier data is securely stored and used only to improve your own quote accuracy. AI learns your actual costs to help create more accurate estimates.
      </AIExplanationBox>
    </div>
  );
}

export default SupplierUpload;
